import { defineStore } from "pinia";
import { authClient, type UserInfo } from "./api/authClient";
import {
  APIError,
  ErrorCategory,
  isAuthServiceUnavailableError,
} from "./api/errors";
import { useConfigStore } from "./store";
import type { MessageKey } from "./i18n";
import { translateCurrent } from "./i18n/current";

interface LoadSettingsOptions {
  shouldApply?: () => boolean;
}

let pendingLogoutRequest: Promise<void> | null = null;

const OAUTH_EMAIL_ALREADY_REGISTERED_ERROR_CODE =
  "oauth_email_already_registered";
const OAUTH_LOGIN_FAILED_ERROR_CODE = "oauth_login_failed";
const GENERIC_OAUTH_ERROR_KEY = "auth.genericError" as const;
const OAUTH_PUBLIC_ERROR_KEYS: Record<string, MessageKey> = {
  [OAUTH_EMAIL_ALREADY_REGISTERED_ERROR_CODE]:
    "auth.oauthEmailAlreadyRegistered",
  [OAUTH_LOGIN_FAILED_ERROR_CODE]: GENERIC_OAUTH_ERROR_KEY,
};

function normalizeOAuthCallbackErrorCode(errorCode: string | null): string {
  const candidate = errorCode?.trim();
  if (
    candidate &&
    Object.prototype.hasOwnProperty.call(OAUTH_PUBLIC_ERROR_KEYS, candidate)
  ) {
    return candidate;
  }
  return OAUTH_LOGIN_FAILED_ERROR_CODE;
}

async function waitForPendingLogout(): Promise<void> {
  if (pendingLogoutRequest) {
    await pendingLogoutRequest;
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    isAuthenticated: authClient.isAuthenticated(),
    user: authClient.getUser() as UserInfo | null,
    isLoading: false,
    hasCheckedAuth: false,
    authStateVersion: 0,
    error: null as string | null,
    errorCode: null as string | null,
  }),

  actions: {
    async register(email: string, password: string): Promise<boolean> {
      this.authStateVersion += 1;
      this.isLoading = true;
      this.error = null;
      this.errorCode = null;

      try {
        const response = await authClient.register({ email, password });
        if (response.success) {
          this.errorCode = null;
          return true;
        } else {
          this.error =
            response.error || translateCurrent("auth.registrationFailed");
          this.errorCode = response.error_code ?? null;
          return false;
        }
      } catch (err) {
        if (err instanceof APIError) {
          this.errorCode = err.errorCode ?? null;
          this.error =
            err.category === ErrorCategory.NETWORK
              ? translateCurrent("auth.networkError")
              : err.message || translateCurrent("auth.registrationFailed");
        } else {
          this.error = translateCurrent("auth.networkError");
          this.errorCode = null;
        }
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async login(email: string, password: string): Promise<boolean> {
      await waitForPendingLogout();
      this.authStateVersion += 1;
      const authRequestVersion = this.authStateVersion;
      this.isLoading = true;
      this.error = null;
      this.errorCode = null;

      try {
        const response = await authClient.login(
          { email, password },
          { updateLocalAuthHint: false }
        );
        if (authRequestVersion !== this.authStateVersion) {
          return false;
        }

        if (response.success) {
          const user = { id: response.user_id ?? "", email };
          this.isAuthenticated = true;
          this.user = user;
          authClient.saveUserHint(user);
          this.hasCheckedAuth = true;
          // 로그인 성공 후 사용자 설정 로드 및 적용
          await this.loadAndApplySettings({
            shouldApply: () => authRequestVersion === this.authStateVersion,
          });
          return authRequestVersion === this.authStateVersion;
        } else {
          this.error = response.error || translateCurrent("auth.loginFailed");
          this.errorCode = response.error_code ?? null;
          return false;
        }
      } catch (err) {
        if (err instanceof APIError) {
          this.errorCode = err.errorCode ?? null;
          this.error =
            err.category === ErrorCategory.NETWORK
              ? translateCurrent("auth.networkError")
              : err.message || translateCurrent("auth.loginFailed");
        } else {
          this.error = translateCurrent("auth.networkError");
          this.errorCode = null;
        }
        return false;
      } finally {
        if (authRequestVersion === this.authStateVersion) {
          this.isLoading = false;
        }
      }
    },

    async resendEmailVerification(email: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.errorCode = null;

      try {
        const response = await authClient.resendVerification(email);
        if (response.success) {
          return true;
        }
        this.error =
          response.error || translateCurrent("auth.resendVerificationFailed");
        this.errorCode = response.error_code ?? null;
        return false;
      } catch (err) {
        if (err instanceof APIError) {
          this.errorCode = err.errorCode ?? null;
          this.error =
            err.category === ErrorCategory.NETWORK
              ? translateCurrent("auth.networkError")
              : err.message ||
                translateCurrent("auth.resendVerificationFailed");
        } else {
          this.error = translateCurrent("auth.networkError");
          this.errorCode = null;
        }
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async logout(): Promise<void> {
      this.authStateVersion += 1;
      const authRequestVersion = this.authStateVersion;
      this.isLoading = true;

      const logoutRequest = authClient.logout({ updateLocalAuthHint: false });
      const trackedLogoutRequest = logoutRequest.finally(() => {
        if (pendingLogoutRequest === trackedLogoutRequest) {
          pendingLogoutRequest = null;
        }
      });
      pendingLogoutRequest = trackedLogoutRequest;

      try {
        await trackedLogoutRequest;
      } catch (error) {
        if (isAuthServiceUnavailableError(error)) {
          if (authRequestVersion === this.authStateVersion) {
            this.error = translateCurrent("auth.logoutFailed");
            this.errorCode = "auth_service_unavailable";
            this.isLoading = false;
          }
          return;
        }
      }

      if (authRequestVersion === this.authStateVersion) {
        this.isAuthenticated = false;
        this.user = null;
        authClient.clearAuth();
        this.hasCheckedAuth = true;
        this.isLoading = false;
      }
    },

    // LOC-596: locktree.pro / www.locktree.pro 처럼 배포가 여러 호스트를 서빙하면
    // 세션 쿠키는 Domain 속성으로 호스트 간 공유되지만, 로그인 힌트(auth_user)는
    // localStorage 에 저장돼 호스트(origin)마다 독립이다. 힌트 부재를 "미인증"으로
    // 단정하면 다른 호스트에서 로그인한 사용자가 이 호스트에서는 로그아웃으로 보인다 —
    // 항상 서버(/api/auth/me)에 물어 실제 세션 쿠키 기준으로 판정한다.
    async checkAuth(): Promise<void> {
      const authCheckVersion = this.authStateVersion;
      this.isLoading = true;
      try {
        const response = await authClient.me({ updateLocalAuthHint: false });
        if (authCheckVersion !== this.authStateVersion) {
          return;
        }

        if (response.success && response.user) {
          this.isAuthenticated = true;
          this.user = response.user;
          authClient.saveUserHint(response.user);
          // 인증 확인 시 사용자 설정 로드 및 적용
          await this.loadAndApplySettings({
            shouldApply: () => authCheckVersion === this.authStateVersion,
          });
        } else {
          this.isAuthenticated = false;
          this.user = null;
          authClient.clearAuth();
        }
      } catch (error) {
        if (authCheckVersion !== this.authStateVersion) {
          return;
        }

        if (!isAuthServiceUnavailableError(error)) {
          this.isAuthenticated = false;
          this.user = null;
          authClient.clearAuth();
        }
      } finally {
        if (authCheckVersion === this.authStateVersion) {
          this.hasCheckedAuth = true;
          this.isLoading = false;
        }
      }
    },

    clearError(): void {
      this.error = null;
      this.errorCode = null;
    },

    applyOAuthCallbackError(errorCode: string | null): void {
      const normalizedCode = normalizeOAuthCallbackErrorCode(errorCode);
      this.errorCode = normalizedCode;
      this.error = translateCurrent(
        OAUTH_PUBLIC_ERROR_KEYS[normalizedCode] ?? GENERIC_OAUTH_ERROR_KEY
      );
    },

    async loadAndApplySettings(
      options: LoadSettingsOptions = {}
    ): Promise<void> {
      try {
        const response = await authClient.getSettings({
          updateLocalAuthHint: false,
        });
        if (response.success && response.settings) {
          if (options.shouldApply && !options.shouldApply()) {
            return;
          }

          // eslint-disable-next-line react-hooks/rules-of-hooks -- Pinia store accessor (defineStore), not a React hook
          const configStore = useConfigStore();

          // 존재하는 경우 config store에 설정 적용
          if (response.settings.startingPot !== undefined) {
            configStore.startingPot = response.settings.startingPot;
          }
          if (response.settings.effectiveStack !== undefined) {
            configStore.effectiveStack = response.settings.effectiveStack;
          }
          if (response.settings.rakePercent !== undefined) {
            configStore.rakePercent = response.settings.rakePercent;
          }
          if (response.settings.rakeCap !== undefined) {
            configStore.rakeCap = response.settings.rakeCap;
          }
        }
      } catch (error) {
        console.error("Failed to load and apply settings:", error);
      }
    },
  },
});
